import produce from 'immer';
import qs from 'qs';
import { change } from 'redux-form';

import {
  INQUIRY_TEMPLATE_EDITOR_DRAFT_PLACEHOLDER_ID,
  INQUIRY_TEMPLATE_EDITOR_FORM_NAME,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import {
  BuilderName,
  BuilderPreview,
  InquiryTemplate,
  InquiryTemplateBuilders,
  InquiryTemplateFiltersResponse,
  InquiryTemplateStatus,
} from 'dashboard/models/inquiry-template';
import {
  InquiryTemplateVersion,
  InquiryTemplateVersionStatus,
  PartialInquiryTemplateVersion,
} from 'dashboard/models/inquiry-template-version';
import { Template, TemplateFiltersResponse } from 'dashboard/models/template';
import { dynamicFlowTemplatesOnlySelector } from 'dashboard/redux/selectors/inquiry-template';
import {
  setInquiryTemplateTreeData,
  setLocalDraftPartialInquiryTemplateVersion,
} from 'dashboard/redux/slices/inquiry-template';
import {
  InquiryTemplatesFilterParams,
  setInquiryTemplateBuilders,
  setInquiryTemplateFilters,
  setInquiryTemplates,
  setLegacyTemplateFilters,
  setLegacyTemplates,
  setPartialInquiryTemplateVersions,
  setTemplateBuilderPreview,
} from 'dashboard/redux/slices/inquiry-templates';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { ApiOptions } from 'lib/clients/ApiClient';
import { ApiEndpoint, ApiListResponse, ApiModel, PaginationParams } from 'lib/interfaces/api';

export const loadInquiryTemplatesList =
  ({
    currentPage,
    pageSize,
    sortParams: { sort, shouldSortDescending },
    filters: { omni, status },
    abortSignal,
    dynamicFlowOnly = false,
  }: {
    currentPage: number;
    pageSize: number;
    sortParams: SortParams;
    filters: InquiryTemplatesFilterParams;
    abortSignal?: AbortSignal;
    dynamicFlowOnly?: boolean;
  }): DashboardThunkAction =>
  async (dispatch, getState) => {
    if (dynamicFlowTemplatesOnlySelector(getState()) || dynamicFlowOnly) {
      const { data: inquiryTemplates, meta: paginationParams } =
        await SessionClientDashboard.listResource<InquiryTemplate, PaginationParams>(
          ApiEndpoint.InquiryTemplate,
          {
            filter: {
              status,
              omni,
            },
            sort: `${shouldSortDescending ? '-' : ''}${sort}`,
            page: {
              number: currentPage,
              size: pageSize,
            },
            signal: abortSignal,
          },
        );

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      dispatch(setInquiryTemplates({ inquiryTemplates, paginationParams }));
    } else {
      // No sort -- we do client side sorting when legacy templates are involved.
      // Legacy templates index endpoint ignores pagination.
      const params: ApiOptions = {
        filter: {
          status,
          omni,
        },
        signal: abortSignal,
      };
      const [{ data: inquiryTemplates, meta: paginationParams }, { data: templates }] =
        await Promise.all([
          SessionClientDashboard.listResource<InquiryTemplate, PaginationParams>(
            ApiEndpoint.InquiryTemplate,
            params,
          ),
          new Promise<ApiListResponse<Template, unknown>>((resolve) => {
            if (status?.length === 0 || status?.includes(InquiryTemplateStatus.Active)) {
              resolve(SessionClientDashboard.listResource<Template>(ApiEndpoint.Template, params));
            } else {
              resolve({ data: [] });
            }
          }),
        ]);

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      dispatch(setInquiryTemplates({ inquiryTemplates, paginationParams }));
      dispatch(setLegacyTemplates({ templates }));
    }
  };

export const loadInquiryTemplateFiltersList =
  ({
    sortParams: { sort, shouldSortDescending },
    filters: { omni, status },
  }: {
    sortParams: SortParams;
    filters: InquiryTemplatesFilterParams;
  }): DashboardThunkAction =>
  async (dispatch, getState) => {
    if (dynamicFlowTemplatesOnlySelector(getState())) {
      const { meta } = await SessionClientDashboard.get<InquiryTemplateFiltersResponse>(
        ApiEndpoint.InquiryTemplate,
        'basics',
        '',
        {
          filter: {
            status,
            omni,
          },
          sort: `${shouldSortDescending ? '-' : ''}${sort}`,
        },
      );

      dispatch(setInquiryTemplateFilters({ inquiryTemplateFilters: meta.filters }));
    } else {
      // No sort -- we do client side sorting when legacy templates are involved.
      // Legacy templates index endpoint ignores pagination.
      const params: ApiOptions = {
        filter: {
          status,
          omni,
        },
      };

      const [{ meta: inquiryTemplatesMeta }, { meta: templatesMeta }] = await Promise.all([
        SessionClientDashboard.get<InquiryTemplateFiltersResponse>(
          ApiEndpoint.InquiryTemplate,
          'basics',
          '',
          params,
        ),
        new Promise<TemplateFiltersResponse>((resolve) => {
          if (status?.length === 0 || status?.includes(InquiryTemplateStatus.Active)) {
            resolve(
              SessionClientDashboard.get<TemplateFiltersResponse>(
                ApiEndpoint.Template,
                'basics',
                '',
                params,
              ),
            );
          } else {
            resolve({ meta: { filters: [] } });
          }
        }),
      ]);

      dispatch(setInquiryTemplateFilters({ inquiryTemplateFilters: inquiryTemplatesMeta.filters }));
      dispatch(setLegacyTemplateFilters({ templateFilters: templatesMeta.filters }));
    }
  };

export const loadPartialInquiryTemplateVersions =
  (inquiryTemplateId: string): DashboardThunkAction =>
  async (dispatch) => {
    const { data: partialInquiryTemplateVersions } =
      await SessionClientDashboard.listResource<PartialInquiryTemplateVersion>(
        ApiEndpoint.InquiryTemplateVersion,
        {
          filter: {
            'inquiry-template-id': inquiryTemplateId,
          },
        },
      );

    dispatch(setPartialInquiryTemplateVersions({ partialInquiryTemplateVersions }));
  };

export const addLocalDraftVersion =
  (
    inquiryTemplateVersion: InquiryTemplateVersion,
    partialInquiryTemplateVersions: PartialInquiryTemplateVersion[],
  ): DashboardThunkAction<Promise<{ versionId: string }>> =>
  async (dispatch, getState) => {
    const latestPartialInquiryTemplateVersion =
      partialInquiryTemplateVersions[partialInquiryTemplateVersions.length - 1];

    const draftInquiryTemplateVersionId =
      latestPartialInquiryTemplateVersion.attributes.status === InquiryTemplateVersionStatus.Draft
        ? latestPartialInquiryTemplateVersion.id
        : null;

    const localDraftPartialInquiryTemplateVersion: PartialInquiryTemplateVersion = {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      id: draftInquiryTemplateVersionId,
      type: ApiModel.PartialInquiryTemplateVersion,
      attributes: {
        name: inquiryTemplateVersion.attributes.name,
        'name-display': inquiryTemplateVersion.attributes['name-display'],
        tier: inquiryTemplateVersion.attributes.tier,
        status: InquiryTemplateVersionStatus.Draft,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        description: null,
        live: false,
      },
    };

    dispatch(
      setLocalDraftPartialInquiryTemplateVersion({
        localDraftPartialInquiryTemplateVersion,
        restoredFrom: inquiryTemplateVersion.id,
      }),
    );

    const { schema } = getState().inquiryTemplate.inquiryTemplateTreeData;
    const currentVersion = await produce(inquiryTemplateVersion, async (newDraftVersion) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      newDraftVersion.id = draftInquiryTemplateVersionId;
      newDraftVersion.attributes.status = InquiryTemplateVersionStatus.Draft;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      newDraftVersion.attributes.description = null;
      if (draftInquiryTemplateVersionId) {
        const { data: originalDraftVersion } =
          await SessionClientDashboard.getResource<InquiryTemplateVersion>(
            ApiEndpoint.InquiryTemplateVersion,
            draftInquiryTemplateVersionId,
          );
        newDraftVersion.attributes['lock-version'] =
          originalDraftVersion.attributes['lock-version'];
      }
    });

    const versionId = draftInquiryTemplateVersionId ?? INQUIRY_TEMPLATE_EDITOR_DRAFT_PLACEHOLDER_ID;
    dispatch(setInquiryTemplateTreeData({ inquiryTemplateVersion: currentVersion, schema }));
    dispatch(change(INQUIRY_TEMPLATE_EDITOR_FORM_NAME, 'restored-from', inquiryTemplateVersion.id));

    return {
      versionId,
    };
  };

export const removeLocalDraftVersion = (): DashboardThunkAction => async (dispatch) => {
  dispatch(
    setLocalDraftPartialInquiryTemplateVersion({
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      localDraftPartialInquiryTemplateVersion: undefined,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      restoredFrom: undefined,
    }),
  );
};

export const loadInquiryTemplateBuilders = (): DashboardThunkAction => async (dispatch) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const { 'supported-builders': supportedBuilders } = (
    await SessionClientDashboard.getResourceMeta<InquiryTemplateBuilders>(
      ApiEndpoint.InquiryTemplate,
      'builders',
    )
  ).meta;
  dispatch(setInquiryTemplateBuilders({ inquiryTemplateBuilders: supportedBuilders }));
};

export const loadTemplateBuilderPreview =
  (builderName: BuilderName): DashboardThunkAction =>
  async (dispatch) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const { steps } = (
      await SessionClientDashboard.getResourceMeta<BuilderPreview>(
        ApiEndpoint.InquiryTemplate,
        `builder-preview?${qs.stringify({ builder: builderName })}`,
      )
    ).meta;
    dispatch(setTemplateBuilderPreview({ builderName, steps }));
  };
